import { memo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { GridContainer, GridItem, Button } from '../../../../components/shared';
import { ACTION_LABELS, A11Y_TEXT, LAYOUT, OPEN_POSTING_CHECKLIST, UI_TEXT } from '../../constants';
import useStyles from './style';

const ActionButtons = memo(
  ({
    isApproved = false,
    canAddNewRow = false,
    isApproveDisabled = false,
    showApproveButton = false,
    selectedProductType = '',
    selectedCurrencyCount = 0,
    selectedSublocationCount = 0,
    hasDateConstraintIssue = false,
    selectedStartDate = null,
    selectedEndDate = null,
    selectedPostingDate = null,
    onAddNewRow,
    onApproveClick,
  }) => {
    const classes = useStyles();

    if (isApproved) {
      return null;
    }

    const hasVisibleActions = canAddNewRow || showApproveButton;
    const hasProductType = Boolean(selectedProductType);
    const hasCurrencies = Number(selectedCurrencyCount) > 0;
    const hasSublocations = Number(selectedSublocationCount) > 0;
    const hasValidSublocations = hasCurrencies && hasSublocations && !hasDateConstraintIssue;
    const hasValidDates = Boolean(selectedStartDate && selectedEndDate && selectedPostingDate);

    const checklistCompletion = {
      product: hasProductType,
      currency: hasCurrencies,
      sublocations: hasValidSublocations,
      dates: hasValidDates,
      apply: showApproveButton,
      approve: false,
    };
    const checklistSteps = OPEN_POSTING_CHECKLIST.map((step) => ({
      ...step,
      completed: Boolean(checklistCompletion[step.key]),
    }));
    const firstIncompleteStepIndex = checklistSteps.findIndex((step) => !step.completed);

    return (
      <Box className={classes.actionButtonsCard} component={Paper}>
        <GridContainer
          spacing={LAYOUT.GRID_SPACING}
          display="flex"
          justifyContent={hasVisibleActions ? 'flex-end' : 'flex-start'}
          alignItems="center"
          py={LAYOUT.ACTION_BUTTONS_PADDING_Y}
          px={LAYOUT.ACTION_BUTTONS_PADDING_X}
        >
          {!hasVisibleActions && (
            <GridItem sx={{ width: '100%' }}>
              <Box className={classes.actionInstructionPanel}>
                <Typography variant="h6" className={classes.actionInstructionTitle}>
                  {UI_TEXT.OPEN_POSTING_HELP_TITLE}
                </Typography>
                <Box component="ul" className={classes.actionInstructionChecklist}>
                  {checklistSteps.map((step, index) => {
                    const isCurrentStep = !step.completed && index === firstIncompleteStepIndex;

                    return (
                      <Box key={step.key} component="li" className={classes.actionInstructionRow}>
                        {step.completed ? (
                          <CheckCircleIcon className={classes.actionInstructionDoneIcon} />
                        ) : (
                          <Box
                            component="span"
                            className={`${classes.actionInstructionStepNumber} ${
                              isCurrentStep ? classes.actionInstructionStepNumberCurrent : ''
                            }`}
                          >
                            {index + 1}
                          </Box>
                        )}
                        <Box className={classes.actionInstructionContent}>
                          <Typography className={classes.actionInstructionItem}>{step.label}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                <Typography variant="body2" className={classes.actionInstructionNote}>
                  {UI_TEXT.OPEN_POSTING_HELP_NOTE}
                </Typography>
              </Box>
            </GridItem>
          )}
          {canAddNewRow && (
            <GridItem>
              <Button
                variant="outlined"
                onClick={onAddNewRow}
                tabIndex={0}
                aria-label={A11Y_TEXT.ADD_NEW_ROW}
                className={classes.addRowButton}
              >
                {ACTION_LABELS.ADD_NEW_ROW}
              </Button>
            </GridItem>
          )}
          {showApproveButton && (
            <GridItem>
              <Button
                variant="contained"
                onClick={onApproveClick}
                disabled={isApproveDisabled}
                tabIndex={0}
                aria-label={A11Y_TEXT.APPROVE_POSTING}
                className={classes.approveButton}
              >
                {ACTION_LABELS.APPROVE}
              </Button>
            </GridItem>
          )}
        </GridContainer>
      </Box>
    );
  },
);

ActionButtons.displayName = 'ActionButtons';

export default ActionButtons;
